import { Icon } from '@advanza/ui'
import InputField from '@trustoo/components/input/InputField'
import HybridTooltip from '@trustoo/components/ui/tooltip/HybridTooltip'
import {
    GUARANTEE_DEFAULT_CHARGE,
    GUARANTEE_DEFAULT_RANKING_MULTIPLIER,
    ServicesGuaranteeSetting,
} from '@trustoo/types/entity/servicesGuaranteeSetting'
import CheckButton from 'components/CheckButton'
import Service from 'interfaces/Services'
import { ChangeEvent } from 'react'

interface Props {
    entity: Service
    onChangeEntity: (diff: Partial<Service>, additional?: Record<string, unknown>) => void
    className?: string
}

/**
 * Trustoo Guaranteed settings
 *
 * Activates the insured job product for this service and defines what it costs the
 * consumer and how much it lifts a Guaranteed provider in the ranking. Saved with the
 * service, so the settings only reach the server when the Save button is used.
 */
const GuaranteeSettings = ({ entity, onChangeEntity, className }: Props) => {
    const settings: ServicesGuaranteeSetting = entity.services_guarantee_setting ?? {
        service_id: entity.service_id,
        enabled: false,
        charge: GUARANTEE_DEFAULT_CHARGE,
        ranking_multiplier: GUARANTEE_DEFAULT_RANKING_MULTIPLIER,
    }
    // Errors are only set on the entity once saving was attempted.
    const error = entity._errors?.services_guarantee_setting
    const errorMessage = (() => {
        if (typeof error === 'string') {
            return error
        }

        return error ? 'Check the Trustoo Guaranteed settings' : undefined
    })()

    const change = (diff: Partial<ServicesGuaranteeSetting>) =>
        onChangeEntity({ services_guarantee_setting: { ...settings, ...diff } })

    return (
        <div className={className}>
            <div className="flex gap-2 items-center">
                <b>Trustoo Guaranteed</b>
                <HybridTooltip
                    className="z-[101]"
                    childrenContent={
                        <div>
                            Trustoo Guaranteed lets eligible Premium providers offer an insured job,
                            which we insure up to € 1.000. It is activated per pro and per service
                            and NL only.
                            <ul className="list-disc ml-4 mt-4">
                                <li>
                                    While the service is off, no provider in it can switch
                                    Guaranteed on.
                                </li>
                                <li>
                                    The charge is the extra fee the pro pays per match when
                                    Guaranteed is active.
                                </li>
                                <li>
                                    The multiplier is the ranking boost a Guaranteed provider gets
                                    in this service, comparable to the lead booster factor.
                                </li>
                            </ul>
                        </div>
                    }>
                    <span>
                        <Icon name="info" fontSize={16} />
                    </span>
                </HybridTooltip>
            </div>
            <div className="my-2">
                <CheckButton
                    name="enabled"
                    msg="Enable Trustoo Guaranteed"
                    checked={settings.enabled}
                    value={!settings.enabled}
                    onChange={() => change({ enabled: !settings.enabled })}
                />
            </div>
            {/* The charge is required while Guaranteed is switched off too, so a rejected
                save has to show the fields even then: otherwise there is nothing to fix. */}
            {(settings.enabled || errorMessage) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 my-2">
                    <InputField
                        legend="Charge (€)"
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.charge}
                        error={errorMessage}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            change({ charge: e.target.value })
                        }
                    />
                    <InputField
                        legend="Ranking multiplier"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={settings.ranking_multiplier}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            change({ ranking_multiplier: e.target.value })
                        }
                    />
                </div>
            )}
        </div>
    )
}

export default GuaranteeSettings
